import {
  AddressSource,
  ProgramEvent,
  ProgramEventCreateParams,
  ProgramEventSeries,
  ProgramEventType,
  ProgramFeatures,
  Venue,
} from '@citruscamps/citrus-client'
import { IANAZone } from 'luxon'
import isURL from 'validator/lib/isURL'
import * as Yup from 'yup'
import translations from '../../../translations/en.json'
import { DateTimeInterval } from '../pages/AppointmentTypeCreate/components/AppointmentTypeSettingsStep'
import { isHistoryViewValid } from '../../../components/FeatureFlag/utils/isHistoryViewValid'

export const VenueDefinition = Yup.object({
  source: Yup.mixed<AddressSource>()
    .oneOf(['mapbox', 'custom'], (translations as any)['error.default_inputs_message'])
    .required((translations as any)['error.default_inputs_message']),
  place_type: Yup.string().notRequired().nullable(),
  place_name: Yup.string().notRequired().nullable(),
  latitude: Yup.string().notRequired().nullable(),
  longitude: Yup.string().notRequired().nullable(),
  address_line_1: Yup.string()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'custom',
      then: Yup.string().required((translations as any)['error.address_required']),
    }),
  address_line_2: Yup.string().notRequired().nullable(),
  city: Yup.string()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'custom',
      then: Yup.string().required((translations as any)['error.address_city_required']),
    }),
  region_short_code: Yup.string()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'custom',
      then: Yup.string().required((translations as any)['error.address_region_required']),
    }),
  postcode: Yup.string()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'custom',
      then: Yup.string().required((translations as any)['error.address_postcode_required']),
    }),
  country_short_code: Yup.string()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'custom',
      then: Yup.string().required((translations as any)['error.address_country_required']),
    }),
  geocode_feature: Yup.object()
    .notRequired()
    .nullable()
    .when('source', {
      is: 'mapbox',
      then: Yup.object().required((translations as any)['error.address_required']),
    }),
})

export const OnlineEventUrlDefinition = Yup.string()
  .required((translations as any)['error.event_online_event_url_required'])
  .test('invalid', (translations as any)['error.event_online_event_url_invalid'], function (val) {
    return !!val && isURL(val)
  })

type EventBaseShape = Record<
  keyof Pick<
    ProgramEventCreateParams | ProgramEvent,
    'type' | 'name' | 'online_event' | 'venues' | 'online_event_url' | 'timezone'
  >,
  Yup.AnySchema
>
const BaseEventLogisticsDefinition = {
  type: Yup.string()
    .oneOf(['camp', 'session'], (translations as any)['error.default_inputs_message'])
    .required((translations as any)['error.event_type_required']),
  name: Yup.string()
    .required((translations as any)['error.name_required'])
    .max(100, (translations as any)['error.event_name_maxlength']),
  online_event: Yup.boolean(),
  venues: Yup.array<Venue>().when('online_event', {
    is: false,
    then: Yup.array<Venue>().of(VenueDefinition),
    otherwise: Yup.array<Venue>().notRequired(),
  }),
  online_event_url: Yup.string().when('online_event', {
    is: true,
    then: OnlineEventUrlDefinition,
    otherwise: Yup.string().notRequired(),
  }),
  timezone: Yup.string().test(
    'timezone',
    (translations as any)['error.event_timezone_invalid'],
    (val) => !val || IANAZone.isValidZone(val),
  ),
} satisfies EventBaseShape

type EventLogisticsShape = Record<
  | keyof Pick<
      ProgramEventCreateParams | ProgramEvent,
      | 'starts_at'
      | 'ends_at'
      | 'is_series'
      | 'has_external_registration'
      | 'external_registration_url'
      | 'feedback_enabled'
    >
  | keyof EventBaseShape,
  Yup.AnySchema
>
const getEventLogisticsDefinition = ({
  features,
}: Required<Pick<IEventSchemaOptions, 'features'>>): EventLogisticsShape => ({
  ...BaseEventLogisticsDefinition,
  starts_at: Yup.date()
    .test('required', (translations as any)['error.event_starts_at_required'], function (val) {
      return this.parent.is_series || !!val
    })
    .test('max', (translations as any)['error.option_start_after_end'], function (val) {
      return this.parent.is_series || (!!val && val < this.parent.ends_at)
    }),
  ends_at: Yup.date()
    .required((translations as any)['error.event_ends_at_required'])
    .test('max', (translations as any)['error.option_start_after_end'], function (val) {
      return this.parent.is_series || (!!val && val > this.parent.starts_at)
    })
    .test('min', (translations as any)['error.event_ends_at_invalid_plan'], function (val) {
      return !val || isHistoryViewValid(val, features)
    }),
  is_series: Yup.boolean().required((translations as any)['error.default_inputs_message']),
  timezone: Yup.string().test(
    'timezone',
    (translations as any)['error.event_timezone_invalid'],
    (val) => !val || (!!val && IANAZone.isValidZone(val)),
  ),
  has_external_registration: Yup.boolean().test(
    'feature_not_enabled',
    (translations as any)['error.default_inputs_message'],
    function (val) {
      return !(!features?.external_registration_enabled && !!val)
    },
  ),
  external_registration_url: Yup.string().when('has_external_registration', {
    is: true,
    then: Yup.string()
      .required((translations as any)['error.url_required'])
      .test('invalid', (translations as any)['error.url_invalid'], function (val) {
        return !!val && isURL(val, { require_protocol: true })
      }),
    otherwise: Yup.string().notRequired(),
  }),
  feedback_enabled: Yup.boolean().required((translations as any)['error.default_inputs_message']),
})

const EventScheduleDefinition = {
  starts_at: Yup.date()
    .test('required', (translations as any)['error.event_starts_at_required'], function (val) {
      return this.parent.is_series || !!val
    })
    .test('min', (translations as any)['error.event_starts_at_past'], function (val) {
      return this.parent.is_series || (!!val && val > new Date())
    })
    .test('max', (translations as any)['error.option_start_after_end'], function (val) {
      return this.parent.is_series || (!!val && val < this.parent.ends_at)
    }),
  ends_at: Yup.date()
    .required((translations as any)['error.event_ends_at_required'])
    .test('max', (translations as any)['error.option_start_after_end'], function (val) {
      return this.parent.is_series || (!!val && val > this.parent.starts_at)
    }),
  is_series: Yup.boolean().required((translations as any)['error.default_inputs_message']),
  series_dates: Yup.array<ProgramEventSeries>().test(
    'required',
    (translations as any)['error.event_series_dates_required'],
    function (val) {
      return !this.parent.is_series || (!!val && val.length > 0)
    },
  ),
  timezone: Yup.string().test(
    'timezone',
    (translations as any)['error.event_timezone_invalid'],
    (val) => !val || IANAZone.isValidZone(val),
  ),
}

export const MaxSummaryLength: number = 140

export class EventDetailsSchema {
  static get(type: ProgramEventType) {
    return Yup.object().shape({
      summary: Yup.string()
        .required((translations as any)['error.event_summary_required'])
        .max(MaxSummaryLength, (translations as any)['error.event_summary_maxlength']),
      description: Yup.string(),
      picture: Yup.object().test(
        'required',
        (translations as any)['error.event_picture_invalid'],
        function (val) {
          return !val || !!val.id
        },
      ),
      show_remaining: Yup.boolean().required((translations as any)['error.default_inputs_message']),
      option: Yup.object()
        .required((translations as any)['error.option_required'])
        .shape({
          cost: Yup.object()
            .shape({
              cost: Yup.object().shape({
                major_value: Yup.number(),
              }),
            })
            .test('required', (translations as any)['error.option_cost_required'], function (val) {
              return (
                type !== 'camp' ||
                (!!val && typeof val.cost?.major_value !== 'undefined') ||
                this.createError({
                  path: 'option.cost.cost.major_value',
                  message: (translations as any)['error.option_cost_required'],
                })
              )
            })
            .test('min', (translations as any)['error.option_cost_invalid'], function (val) {
              return (
                type !== 'camp' ||
                (typeof val?.cost?.major_value !== 'undefined' && val.cost.major_value >= 0) ||
                this.createError({
                  path: 'option.cost.cost.major_value',
                  message: (translations as any)['error.option_cost_invalid'],
                })
              )
            }),
          quantity_total: Yup.number()
            .required((translations as any)['error.option_quantity_required'])
            .positive((translations as any)['error.option_quantity_min'])
            .integer((translations as any)['error.option_quantity_invalid']),
          include_fee: Yup.boolean().required(
            (translations as any)['error.option_include_fee_required'],
          ),
        }),
    })
  }
}

const DateOverrideDefinition = {
  intervals: Yup.array<DateTimeInterval>().of(
    Yup.object()
      .shape({
        from: Yup.object()
          .required((translations as any)['error.starts_at_time_required'])
          .test('max', (translations as any)['error.interval_from_max'], function (val) {
            return !this.parent.to || (!!val && val <= this.parent.to)
          }),
        to: Yup.object()
          .required((translations as any)['error.ends_at_time_required'])
          .test('min', (translations as any)['error.interval_to_max'], function (val) {
            return !this.parent.from || (!!val && val >= this.parent.from)
          }),
      })
      .required(),
  ),
  selectedDates: Yup.array().of(Yup.object()).notRequired(),
}

type SchemaType = 'logistics' | 'details' | 'schedule' | 'date_override' | 'base'
type EventShape = EventLogisticsShape
interface IEventSchemaOptions {
  schema?: SchemaType
  features?: Pick<ProgramFeatures, 'external_registration_enabled' | 'months_of_history'>
}
export class EventSchema {
  static get(options: IEventSchemaOptions) {
    return Yup.object().shape(EventSchema.shape(options))
  }

  static shape(
    options: Omit<IEventSchemaOptions, 'schema'>,
  ): Record<keyof EventShape, Yup.AnySchema>
  static shape(
    options: Omit<Required<IEventSchemaOptions>, 'schema'> & { schema: 'logistics' },
  ): Record<keyof EventLogisticsShape, Yup.AnySchema>
  static shape(options: IEventSchemaOptions): Partial<Record<keyof EventShape, Yup.AnySchema>>
  static shape(options: IEventSchemaOptions) {
    const { schema, features } = options
    let shape: Partial<Record<keyof EventShape, Yup.AnySchema>> = {}
    if (!schema || schema === 'logistics') {
      if (!features) throw new Error('Features are required for logistics schema')
      shape = {
        ...shape,
        ...getEventLogisticsDefinition({ features }),
      } satisfies EventLogisticsShape
    }
    if (!schema || schema === 'base') {
      shape = { ...shape, ...BaseEventLogisticsDefinition }
    }
    if (!schema || schema === 'schedule') {
      shape = { ...shape, ...EventScheduleDefinition }
    }
    if (!schema || schema === 'details') {
      shape = { ...shape, ...EventScheduleDefinition }
    }
    if (!schema || schema === 'date_override') {
      shape = { ...shape, ...DateOverrideDefinition }
    }
    return shape
  }

  static fields(options: IEventSchemaOptions) {
    return Object.keys(EventSchema.shape(options))
  }
}
